import { BaseShapeDataModel, BehaviorType, ICTTLByHslColorTransform, IPageDataModel, convertHSLToRGB, convertRGBToHSL } from '@tencent/slide-sdk-model';
import { AttrName, ColorType, IAnimKeyframeHandler, IBehavior } from '../interface';
import { STSchemeColorVal, ICTColorDmlA } from '@tencent/slide-sdk-pb-model';
import { hexToRgba, colorStrToColorDmlA, rgbStrToObject } from '@src/slide/infrastructure/module/independent/color/transform';
import { AnimCanvasProperty } from '../../anim-constants';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IConverterToStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { getFillColor, getFontColor, getLineColor, getFontSize, calculateHSLColor } from '../../anim-utils';


export default class BaseAnimKeyframeHandler<T extends IBehavior, M extends IElementBaseModel>
implements IAnimKeyframeHandler<T, M> {
    protected behavior: T;
    protected elementModel: IElementBaseModel;
    protected behaviorType: BehaviorType;

    public parse(
        behavior: T,
        duration: number,
        elementModel: M,
    ): IConverterToStyle | undefined {
        this.behavior = behavior;
        this.elementModel = elementModel;
        return;
    }
    protected getKeyframeData(duration: number): IConverterToStyle| undefined {
        const { cBhvr } = this.behavior;
        const tempToStyle = {};
        // 通过cBhvr.cTn，获取属性持续时间
        if (!cBhvr?.cTn?.dur) return;
        const childAnimDur = Number(cBhvr.cTn.dur); // 以ms为单位

        // 获取属性开始变化时间
        const delay =  cBhvr?.cTn?.stCondLst?.cond?.[0]?.delay ?? 0;
        const rate =  Number(delay) / duration;
        if (rate < 0 || rate > 1) return;

        //  通过cBhvr?.attrNameLst，获取属性名
        const attrName = cBhvr?.attrNameLst?.attrName?.[0] as AttrName;
        if (!attrName) return;

        const attrNameMap = {
            [AttrName.FILL_COLOR]: AnimCanvasProperty.FILL_COLOR,
            [AttrName.FONT_COLOR]: AnimCanvasProperty.FONT_COLOR,
            [AttrName.LINE_COLOR]: AnimCanvasProperty.LINE_COLOR,
            [AttrName.FONT_SIZE]: AnimCanvasProperty.FONT_SIZE,
        };
        const keyName = attrNameMap[attrName];
        if (!keyName) return;

        // 获取属性值
        const attrValue = this.convertAttrValue(attrName);
        if (!attrValue) return;

        // 组装数据
        tempToStyle[keyName] = [{ value: attrValue, duration: childAnimDur, rate }];

        // 是否要逆序播放
        if (!cBhvr?.cTn?.autoRev) return tempToStyle;
        tempToStyle[keyName].push({
            value: this.getOriginalValue(attrName),
            duration: childAnimDur,
            rate: 1 - rate,
        });
        return tempToStyle;
    }

    /**
     * 转换属性值
     * @param attrName
     * @param options
     * @returns
     */
    protected convertAttrValue(attrName: AttrName): string | number | undefined {
        const handlers = {
            [AttrName.FILL_COLOR]: () => this.convertToRgbaColor(attrName),
            [AttrName.FONT_COLOR]: () => this.convertToRgbaColor(attrName),
            [AttrName.LINE_COLOR]: () => this.convertToRgbaColor(attrName),
            [AttrName.FONT_SIZE]: () => this.convertFontSize(),
            [AttrName.FONT_WEIGHT]: () => this.convertFontWeight(),
        };

        return handlers[attrName]?.();
    }

    /**
     * 获取元素属性初始值
     * @param attrName 属性名
     * @returns string | number | undefined
     */
    protected getOriginalValue(attrName: AttrName): string | number | undefined {
        const handlers = {
            [AttrName.FILL_COLOR]: () => getFillColor(this.elementModel)?.rgba,
            [AttrName.FONT_COLOR]: () => getFontColor(this.elementModel)?.rgba,
            [AttrName.LINE_COLOR]: () => getLineColor(this.elementModel)?.rgba,
            [AttrName.FONT_SIZE]: () => getFontSize(this.elementModel),
            [AttrName.FONT_WEIGHT]: () => this.getFontWeight(),
        };
        return handlers[attrName]?.();
    }

    /**
     * 将sdk中颜色值转换为rgba
     * @param colorDmlA
     * @returns rgba颜色值，例：'rgba(0,0,0,1)' | undefined
     * clrSpc: 1， 有to 无by，to的值的为 ICTColorDmlA，例如：强调动画-填充颜色动画
     * clrSpc：2， 有by 无to, by的值类型为 ICTTLByAnimateColorTransform， 例如：强调动画-补色
     * clrSpc: undefined, 有to 无by，to的值类型为 ICTTLAnimVariant， 例如：强调动画-画笔颜色
     */
    protected convertToRgbaColor(attrName: AttrName): string | undefined {
        return;
    }

    /**
     * 将sdk中的主题色转换为rgba
     * @param schemeClrVal STSchemeColorVal
     * @returns rgba颜色值，例：rgba(0,0,0,1)  | undefined
     */
    protected convertSchemeClrToRgba(schemeClrVal?: STSchemeColorVal): string | undefined {
        if (!schemeClrVal) return;
        const pageDataModel = this.getPageDataModel();
        if (!pageDataModel) return;
        const colorManager = SlideDataServices.SDKDataServices.ColorManager();
        const colorDmlA = colorStrToColorDmlA(STSchemeColorVal[Number(schemeClrVal)]);
        const srgb = colorManager?.resolveColor(colorDmlA, pageDataModel)?.srgbClr?.val;
        return hexToRgba(`#${srgb}`);
    }

    /**
     * 将sdk中的16进制颜色值转换为rgba
     * @param srgbClrVal sdk数据中的颜色值,例如'000000'
     * @returns rgba颜色值，例：'rgba(0,0,0,1)' | undefined
     */
    protected convertSrgbClrToRgba(srgbClrVal?: string): string | undefined {
        if (!srgbClrVal) return;
        return hexToRgba(`#${srgbClrVal}`);
    }

    /**
     * 将sdk中的RGBSpace颜色值转换为rgba颜色值
     * @param colorDmlA sdk中的颜色值
     * @returns rgba颜色值，例如：rgba(255, 255, 255, 1) | undefined
     */
    protected handleRGBSpace(colorDmlA?: ICTColorDmlA): string | undefined {
        if (!colorDmlA) return;
        const { schemeClr, srgbClr } = colorDmlA;
        const handlers = {
            [ColorType.SCHEME]: () => this.convertSchemeClrToRgba(schemeClr?.val),
            [ColorType.SRGB]: () => this.convertSrgbClrToRgba(srgbClr?.val),
        };

        let color;

        Object.keys(colorDmlA).forEach((key) => {
            const handler = handlers[key];
            if (handler) {
                color = handler();
            }
        });

        return color;
    }


    /**
     * 将sdk中的HSLSpace颜色值转换为rgba颜色值
     * @param attrName 属性名
     * @param hsl sdk中的hsl值
     * @returns rgba颜色值，例如：rgba(255, 255, 255, 1) | undefined
     */
    protected handleHSLSpace(attrName: AttrName, hsl?: ICTTLByHslColorTransform): string | undefined {
        return calculateHSLColor(hsl, this.getOriginalColor(attrName));
    }


    protected convertFontSize(): number | undefined {
        const multiple = Number(this.behavior.to);
        const originalFontSize = getFontSize(this.elementModel);
        return originalFontSize && originalFontSize * multiple;
    }


    protected convertFontWeight(): number | undefined {
        // TODO 待扩展
        return;
    }

    protected getFontWeight(): number | undefined {
        // TODO 待扩展
        return;
    }


    /**
     * 获取atrrName对应的颜色
     * @param attrName
     * @returns ITDColor | undefined
     */
    protected getOriginalColor(attrName: AttrName):  ITDColor | undefined {
        const map = {
            [AttrName.FILL_COLOR]: () => getFillColor(this.elementModel),
            [AttrName.FONT_COLOR]: () => getFontColor(this.elementModel),
            [AttrName.LINE_COLOR]: () => getLineColor(this.elementModel),
        };

        return map[attrName]?.();
    }

    /**
     * 获取元素所在页面的sdk数据，用于获取正确的主题色
     * @returns IPageDataModel | undefined
     */
    private getPageDataModel(): IPageDataModel | undefined {
        let pageDataModel;
        if (this.elementModel?.parent?.type === UIModelType.TABLE) {
            pageDataModel = (this.elementModel.parent.sdkData as BaseShapeDataModel)?.getPage?.();
        } else {
            pageDataModel = this.elementModel?.sdkData?.getPage?.();
        }

        return pageDataModel;
    }
}
